import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Component, ReactNode } from "react";
import DailyLogEntry from "./DailyLogEntry";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class DailyLogEntryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An error occurred while loading the daily log entry page.</h2>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
                <div className="font-semibold mb-2">Error Message:</div>
                <div className="text-sm text-destructive mb-4">{this.state.error.message}</div>
                <div className="font-semibold mb-2">Stack Trace:</div>
                <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                  {this.state.error.stack}
                </pre>
              </div>
            )}

            <Button
              onClick={() => {
                // Navigate back to logs
                window.location.href = "/logs";
              }}
              className="mt-4"
            >
              Back to Logs
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function DailyLogEntryWrapper() {
  return (
    <DailyLogEntryErrorBoundary>
      <DailyLogEntry />
    </DailyLogEntryErrorBoundary>
  );
}
